import { Box, Stack } from '@mui/material';
import useCheckPermissionsForCurrentCrmOrg from '../common/permissions/useCurrentCrmOrgPermissions';
import { colors, Tag, Tooltip, Typography } from '@sweep-io/sweep-design';
import { ObjectTypeLabelAndKeyTag } from '../common/ObjectTypeLabelAndKey';
import { getDisplayLineTexts } from './helper';
import ListItemActionCard from '../common/ListItemActionCard';
import { DeployStatusForTable } from '../../types/enums/DeployStatusForTable';
import useObjectTypesWithFetch from '../../hooks/useObjectTypesWithFetch';
import { useAutomationsContext } from './AutomationsContext';
import { AutomationsListProps } from './AutomationsListProps';
import AutomationActionText from './AutomationActionText';
import { translateConditionsToString } from './marketing-attribution-form/helpers';
import { useSweepFieldsInfo } from '../../sweep-fields/useSweepFieldsInfo';
import { memo, useMemo, useCallback } from 'react';
import { ReactNode } from 'react';

export const AutomationsList = memo(
  ({
    automations,
    crmOrgId,
    selectedId,
    onEdit,
    getActionsMenu,
    onActionMenuClick,
    noTriggerText,
  }: AutomationsListProps & {
    selectedId?: string;
    noTriggerText?: boolean;
  }) => {
    const dataByVariant = useAutomationsContext();
    const [isAllowedEdit] = useCheckPermissionsForCurrentCrmOrg([dataByVariant.permissions.edit]);

    const getContentColor = useCallback(
      (active: boolean) => (active ? colors.grey[700] : colors.grey[500]),
      [],
    );
    const getTitleColor = useCallback(
      (active: boolean) => (active ? colors.grey[900] : colors.grey[700]),
      [],
    );

    const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId });
    const { getLabelsFromIds } = useSweepFieldsInfo();

    const displayTextsCache = useMemo(() => {
      const cache = new Map<string, { triggerText: string; actionText: ReactNode[] }>();
      automations?.forEach((automationItem) => {
        const key = `${automationItem.automationId}-${automationItem.versionId}`;
        if (!cache.has(key)) {
          cache.set(key, getDisplayLineTexts(automationItem));
        }
      });
      return cache;
    }, [automations]);

    const processedAutomations = useMemo(() => {
      return automations?.map((automationItem: AutomationStructureNew) => {
        const key = `${automationItem.automationId}-${automationItem.versionId}`;
        const { triggerText, actionText } =
          displayTextsCache.get(key) || getDisplayLineTexts(automationItem);

        const campaignCriteria = (
          automationItem.automationDetails?.actions as MarketingAttributionModelAction[]
        )[0]?.actionParams?.campaignFilter;

        const criteria = automationItem.automationDetails?.when?.criteria;
        const translatedCampaignCriteria = campaignCriteria
          ? translateConditionsToString(campaignCriteria?.criteria, getLabelsFromIds)
          : [];
        const translatedCriteria = criteria
          ? translateConditionsToString(criteria.criteria, getLabelsFromIds)
          : [];

        return {
          automationItem,
          triggerText,
          actionText,
          translatedCampaignCriteria,
          translatedCriteria,
        };
      });
    }, [automations, getLabelsFromIds, displayTextsCache]);

    const handleEdit = useCallback(
      (automationItem: AutomationStructureNew) => {
        onEdit(automationItem);
      },
      [onEdit],
    );

    const automationItemsJsx = useMemo(() => {
      return processedAutomations?.map(
        ({
          automationItem,
          triggerText,
          actionText,
          translatedCampaignCriteria,
          translatedCriteria,
        }) => {
          const isSelected = automationItem.automationId === selectedId;
          const contentColor = getContentColor(automationItem.isActive);
          const titleColor = getTitleColor(automationItem.isActive);

          return (
            <Box key={`${automationItem.automationId}${automationItem.versionId}`}>
              <ListItemActionCard
                isSelected={isSelected}
                title={automationItem.name}
                onLineClick={() => handleEdit(automationItem)}
                isAllowedBtn={isAllowedEdit}
                titleSX={{ color: titleColor }}
                headerRightContent={
                  <>
                    {automationItem.notifyAdmin && (
                      <Tooltip title="Pending admin approval" placement="top">
                        <span>
                          <Tag startIconName="Stamp" iconColor={colors.night[900]} />
                        </span>
                      </Tooltip>
                    )}
                    {!automationItem.isActive && (
                      <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <Typography variant="caption" color={colors.blush[600]}>
                          Inactive
                        </Typography>
                      </Box>
                    )}
                    {automationItem.status === DeployStatusForTable.Pending &&
                      !automationItem.notifyAdmin && (
                        <Tooltip title="Updates pending deploy" placement="top">
                          <span>
                            <Tag startIconName="Refresh" iconColor={colors.night[900]} />
                          </span>
                        </Tooltip>
                      )}
                    {automationItem.status === DeployStatusForTable.Draft &&
                      !automationItem.notifyAdmin && (
                        <Tag startIconName="Edit" label="Draft" iconColor={colors.night[900]} />
                      )}
                    {automationItem.objectName && (
                      <ObjectTypeLabelAndKeyTag
                        label={
                          objectTypesByName[automationItem.objectName]?.label ||
                          automationItem.objectName
                        }
                        objectType={automationItem.objectName}
                        tagVariant="tiny"
                      />
                    )}
                  </>
                }
                content={
                  <Box sx={{ color: contentColor }}>
                    {!noTriggerText && triggerText && `${triggerText}:`}
                    <Stack gap="2px">
                      {actionText.map(
                        (el, index) =>
                          el && (
                            <AutomationActionText key={index} element={el} color={contentColor} />
                          ),
                      )}
                      <Typography variant="body">
                        {translatedCriteria?.map((el, index) =>
                          el ? <AutomationActionText key={index} element={el} /> : null,
                        )}
                        {translatedCampaignCriteria?.map((el, index) =>
                          el ? <AutomationActionText key={index} element={el} /> : null,
                        )}
                        {!translatedCriteria?.length &&
                          !translatedCampaignCriteria?.length &&
                          !actionText?.length &&
                          'No conditions'}
                      </Typography>
                    </Stack>
                  </Box>
                }
                actionsMenu={getActionsMenu(automationItem)}
                onActionMenuClick={(action: any) => onActionMenuClick(automationItem)(action)}
              />
            </Box>
          );
        },
      );
    }, [
      processedAutomations,
      selectedId,
      getContentColor,
      getTitleColor,
      handleEdit,
      isAllowedEdit,
      noTriggerText,
      objectTypesByName,
      getActionsMenu,
      onActionMenuClick,
    ]);

    return (
      <Stack
        className="automations-list-scroll"
        sx={{
          flexDirection: 'column',
          height: `100%`,
          overflowY: 'auto',
        }}
      >
        {automationItemsJsx}
      </Stack>
    );
  },
);
